/**
 * HTTP API client for TarifData backend.
 *
 * Access-token refresh is single-flight, retries the failed request exactly
 * once, and clears the session only when the refresh token is rejected.
 */

import {
  ApiError,
  createSessionClient,
  type SessionRequestOptions,
  type TokenStore,
} from '@tarifdata/api-client';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const tokenStore: TokenStore = {
  getAccessToken: () => localStorage.getItem('access_token'),
  getRefreshToken: () => localStorage.getItem('refresh_token'),
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem('access_token', accessToken);
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
  },
  clearTokens: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  },
};

const sessionClient = createSessionClient({
  baseUrl: API_BASE_URL,
  tokenStore,
  onSessionExpired: () => {
    window.dispatchEvent(new CustomEvent('tarifdata:session-expired'));
    if (window.location.pathname !== '/login') window.location.assign('/login');
  },
});

export const api = {
  get: <T>(endpoint: string, options?: SessionRequestOptions) =>
    sessionClient.get<T>(endpoint, options),

  post: <T>(endpoint: string, body?: unknown, options?: SessionRequestOptions) =>
    sessionClient.post<T>(endpoint, body, options),

  patch: <T>(endpoint: string, body: unknown, options?: SessionRequestOptions) =>
    sessionClient.patch<T>(endpoint, body, options),

  delete: <T>(endpoint: string, options?: SessionRequestOptions) =>
    sessionClient.delete<T>(endpoint, options),

  upload: async <T>(endpoint: string, file: File): Promise<T> => {
    const formData = new FormData();
    formData.append('file', file);
    return sessionClient.request<T>(endpoint, {
      method: 'POST',
      body: formData,
    });
  },

  download: async (endpoint: string, filename: string) => {
    const response = await sessionClient.raw(endpoint);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(anchor);
  },
};

export const refreshAccessToken = () => sessionClient.refreshAccessToken();

export { ApiError };
